/**
 * Booking-detail "Feeding — this stay" section (migration 0101).
 *
 * Shows the plan that applies to the booking (stay override → pet default →
 * legacy text) and a "Customise for this stay" toggle. Turning it on opens
 * the editor pre-filled from the pet default and saves a per-stay override
 * (PUT /api/bookings/:id/feeding-plan); turning it off deletes the override
 * so the pet default applies again.
 */
import { FeedingPlanEditor } from '@/components/FeedingPlanEditor';
import { FeedingPlanSummary } from '@/components/FeedingPlanSummary';
import {
  type BookingFeedingPlanResponse,
  type FeedingPlanDraft,
  deleteBookingFeedingPlan,
  draftFromPlan,
  getBookingFeedingPlan,
  putBookingFeedingPlan,
  validateFeedingDraft,
} from '@/lib/feeding';
import { Loader2, Utensils } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

const SECTION_LABEL = 'text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground';

interface BookingFeedingSectionProps {
  bookingId: string;
}

export function BookingFeedingSection({ bookingId }: BookingFeedingSectionProps) {
  const [data, setData] = useState<BookingFeedingPlanResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<FeedingPlanDraft>(draftFromPlan(null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getBookingFeedingPlan(bookingId));
    } catch (e) {
      setError((e as Error).message || 'Could not load feeding plan');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !data) {
    return <p className="text-sm text-muted-foreground">Loading feeding plan…</p>;
  }
  if (!data) {
    return <p className="text-xs text-destructive">{error ?? 'Could not load feeding plan'}</p>;
  }
  if (!data.feedingApplies) return null;

  const hasOverride = data.override !== null;

  function startOverride() {
    // Pre-fill from whichever plan currently applies so staff tweak, not retype.
    setDraft(draftFromPlan(data?.override ?? data?.petDefault ?? null));
    setEditing(true);
    setError(null);
  }

  async function saveOverride() {
    const issues = validateFeedingDraft(draft);
    if (issues.length > 0) {
      setError(issues.join('. '));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await putBookingFeedingPlan(bookingId, draft);
      setEditing(false);
      await load();
    } catch (e) {
      setError((e as Error).message || 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  async function removeOverride() {
    setBusy(true);
    setError(null);
    try {
      await deleteBookingFeedingPlan(bookingId);
      setEditing(false);
      await load();
    } catch (e) {
      setError((e as Error).message || 'Could not remove override');
    } finally {
      setBusy(false);
    }
  }

  const sourceLabel =
    data.source === 'stay'
      ? 'Customised for this stay'
      : data.source === 'pet'
        ? "Pet's usual plan"
        : data.source === 'legacy_text'
          ? 'Free-text instructions'
          : 'No plan on file';

  return (
    <div data-testid="booking-feeding-section">
      <div className="mb-2 flex items-center gap-2">
        <Utensils className="size-3.5 text-muted-foreground" strokeWidth={1.75} />
        <p className={SECTION_LABEL}>Feeding — this stay</p>
        <span className="ring-hairline ml-auto rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
          {sourceLabel}
        </span>
      </div>

      {editing ? (
        <div className="space-y-3">
          <FeedingPlanEditor value={draft} onChange={setDraft} idPrefix={`stay-${bookingId}`} />
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void saveOverride()}
              disabled={busy}
              className="press inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
            >
              {busy && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
              Save for this stay
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={busy}
              className="press rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <FeedingPlanSummary
            items={data.plan?.items ?? []}
            ownerSupplied={data.plan?.ownerSupplied ?? false}
            allergies={data.plan?.allergies ?? null}
            redFlags={data.plan?.redFlags ?? null}
            legacyText={data.plan ? null : data.legacyText}
            emptyLabel="No feeding plan on file for this pet."
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                className="size-3.5"
                checked={hasOverride}
                disabled={busy}
                onChange={(e) => {
                  if (e.target.checked) startOverride();
                  else void removeOverride();
                }}
                aria-label="Customise feeding for this stay"
              />
              Customise for this stay
            </label>
            {hasOverride && (
              <button
                type="button"
                onClick={startOverride}
                className="press text-xs font-medium text-primary hover:underline"
              >
                Edit stay plan
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
